import { Activity, Bot, ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import styles from './Auth.module.css';

/** Static node network: the prototype animates one; this costs no frames. */
function Network() {
  const nodes = [
    [8, 12], [22, 28], [38, 10], [55, 22], [70, 8], [86, 18], [14, 48], [30, 60], [48, 44], [64, 56], [80, 40], [92, 62],
    [10, 82], [26, 90], [44, 76], [60, 88], [76, 74], [90, 90],
  ];
  const links: Array<[number, number]> = [
    [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [1, 6], [6, 7], [7, 8], [3, 8], [8, 9], [9, 10], [5, 10], [10, 11],
    [6, 12], [12, 13], [13, 14], [7, 14], [14, 15], [9, 15], [15, 16], [16, 17], [11, 17],
  ];
  return (
    <svg className={styles.network} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      {links.map(([a, b], i) => (
        <line key={i} x1={nodes[a]![0]} y1={nodes[a]![1]} x2={nodes[b]![0]} y2={nodes[b]![1]} stroke="currentColor" strokeWidth={0.15} />
      ))}
      {nodes.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={0.55} fill="currentColor" />
      ))}
    </svg>
  );
}

/** Spec 01: brand panel left, form right, one column below 900px. */
export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className={styles.layout}>
      <aside className={styles.brand}>
        <Network />
        <div className={styles.brandTop}>
          <div className={styles.wordmark}>
            <span className={styles.wordmarkTitle}>Titan</span>
            <span className={styles.wordmarkSub}>by DinoCloud</span>
          </div>
        </div>
        <div className={styles.brandMain}>
          <div className={styles.tagline}>operations hub</div>
          <h1 className={styles.headline}>One place to run your cloud operation.</h1>
          <p className={styles.body}>
            Infrastructure and application health, modernization work, cost and ROI, and the AI agents that run alongside your team.
            Your organization is already connected. Sign in to continue.
          </p>
          <ul className={styles.features}>
            <li className={styles.feature}>
              <Activity size={19} strokeWidth={1.75} className={styles.featureIcon} aria-hidden="true" />
              <span>See what is burning, what is exposed and what it costs, read from your AWS accounts.</span>
            </li>
            <li className={styles.feature}>
              <Bot size={19} strokeWidth={1.75} className={styles.featureIcon} aria-hidden="true" />
              <span>Work with Rex, Moddy and Costy, agents that show their evidence before they act.</span>
            </li>
            <li className={styles.feature}>
              <ShieldCheck size={19} strokeWidth={1.75} className={styles.featureIcon} aria-hidden="true" />
              <span>Every access and every change is logged and visible to your team.</span>
            </li>
          </ul>
        </div>
        <div className={styles.brandBottom}>DinoCloud · AWS Premier Tier Services Partner · Latin America and the U.S.</div>
      </aside>
      <section className={styles.panel}>{children}</section>
    </div>
  );
}

export function AuthTitle({ title, description }: { title: string; description?: ReactNode }) {
  return (
    <div>
      <h2 className={styles.title}>{title}</h2>
      {description && <p className={styles.description}>{description}</p>}
    </div>
  );
}
